import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { LocalizeRouterService } from '@gilsdav/ngx-translate-router';
import { TranslateService } from '@ngx-translate/core';
import { OfferInterface } from '@sport-activities/sdk';
import { combineLatest, of, Subscription } from 'rxjs';
import { mergeMap } from 'rxjs/operators';

import { BackLinkInterface } from '~/checkout/interfaces/back-link.interface';
import { CheckoutItem } from '~/checkout/model/checkout-item.model';
import { CheckoutService } from '~/checkout/services/checkout/checkout.service';
import { VtmnPlayButtonShapeEnum } from '~/component/buttons/vtmn-play-button/enum/vtmn-play-button-shape.enum';
import { VtmnPlayButtonSizeEnum } from '~/component/buttons/vtmn-play-button/enum/vtmn-play-button-size.enum';
import { VtmnPlayIconButtonSizeEnum } from '~/component/buttons/vtmn-play-icon-button/enum/vtmn-play-icon-button-size.enum';
import { VtmnPlayIconButtonVariantEnum } from '~/component/buttons/vtmn-play-icon-button/enum/vtmn-play-icon-button-variant.enum';
import { CtaButtonTypeEnum } from '~/component/call-to-action/enums/button-type.enum';
import { CtaWidthEnum } from '~/component/call-to-action/enums/width.enum';
import { ModalComponent } from '~/component/modal/modal.component';
import { VtmnPlayIconTypeEnum } from '~/component/vtmn-play-icon/enums/vtmn-play-icon-type.enum';
import { Activity } from '~/core/model/activity.model';
import { Timeslot } from '~/core/model/timeslot.model';
import { ActivityService } from '~/core/services/activity/activity.service';
import { MatchMediaService } from '~/core/services/match-media/match-media.service';
import { RedirectionService } from '~/core/services/redirection/redirection.service';
import { TitleService } from '~/core/services/title/title.service';
import { ActivityMediaResolutionEnum } from '~/organizer/shared/enums/media-resolution.enum';

@Component({
  selector: 'app-birthday-sport',
  templateUrl: './birthday-sport.component.html',
  styleUrls: ['./birthday-sport.component.scss'],
})
export class BirthdaySportComponent implements OnInit, OnDestroy {
  @ViewChild(ModalComponent) public sportModal: ModalComponent;
  private readonly subscriptions = new Subscription();
  public readonly submitButtonWidth = CtaWidthEnum.Responsive;
  public readonly ctaButtonType = CtaButtonTypeEnum;
  public readonly vtmnPlayButtonSize = VtmnPlayButtonSizeEnum;
  public readonly icon = VtmnPlayIconTypeEnum;
  public readonly vtmnPlayIconButtonSize = VtmnPlayIconButtonSizeEnum;
  public readonly vtmnPlayIconButtonVariant = VtmnPlayIconButtonVariantEnum;
  public readonly vtmnPlayButtonShape = VtmnPlayButtonShapeEnum;

  private newItemSubscription: Subscription;

  public activity: Activity;
  public birthdaySportForm: FormArray;
  public isMobileDevice: boolean;
  public item: CheckoutItem;
  public offer: OfferInterface;
  public selectedSport;
  public sportItems: [] = [];
  public sportInfoModal: {
    description: string;
    identifier: number;
    image: string;
    name: string;
  };
  public timeslot: Timeslot;
  public imageUrl: string;

  constructor(
    private readonly activityService: ActivityService,
    private readonly checkoutService: CheckoutService,
    private readonly route: ActivatedRoute,
    private readonly formBuilder: FormBuilder,
    public readonly elementRef: ElementRef,
    private readonly localizeRouterService: LocalizeRouterService,
    private readonly matchMediaService: MatchMediaService,
    private readonly redirectionService: RedirectionService,
    private readonly titleService: TitleService,
    private readonly translateService: TranslateService
  ) {}
  public ngOnDestroy(): void {
    this.newItemSubscription.unsubscribe();
  }

  public ngOnInit(): void {
    this.birthdaySportForm = this.formBuilder.array([]);

    this.route?.queryParamMap?.subscribe((params) => {
      this.activityService
        .getTimeslot(params.get('sku')) // get timeslot
        .pipe(
          mergeMap((timeslot) => {
            return combineLatest([
              // merge response to retrieve also activity
              of(timeslot),
              this.activityService.getActivity(timeslot.activityIdentifier),
            ]);
          })
        )
        .subscribe(([timeslot, activity]) => {
          this.activity = activity;
          this.timeslot = timeslot;

          this.manageSports(params);
        });
    });

    // subscribe to cart items
    this.newItemSubscription = this.checkoutService
      .newItem()
      .subscribe((checkoutItem) => {
        this.item = checkoutItem;
      });

    this.titleService.setPageTitle(
      'seo.checkout.birthday.sport.selection.title'
    );

    this.birthdaySportForm.valueChanges.subscribe((sports) => {
      this.selectedSport = [];
      sports.map((checkbox) => {
        if (checkbox.checked === true) {
          this.selectedSport.push(checkbox.sport.name);
        }
      });
    });

    this.subscriptions.add(
      this.matchMediaService.isMobileDevice().subscribe((isMobileDevice) => {
        this.isMobileDevice = isMobileDevice;
      })
    );
  }

  public manageSports(params): void {
    this.activity.indicativeOffers?.forEach((offer) => {
      if (params.get('offer') === offer.identifier) {
        this.offer = offer;
        const sports = this.offer?.meta?.sports as unknown as [];
        sports.forEach((sport) => {
          this.addCheckbox(sport);
          this.sportItems.push(sport);
        });
      }
    });
  }

  public showImageModal(id: string): void {
    this.imageUrl = this.activity.getMediaUrlByIdentifier(
      id,
      ActivityMediaResolutionEnum.LARGE
    );
    this.sportModal.show();
  }

  public getSportImage(assetIdentifier: string): string {
    return this.activity.getMediaUrlByIdentifier(
      assetIdentifier,
      ActivityMediaResolutionEnum.LARGE
    );
  }

  public get backLink(): BackLinkInterface {
    return {
      path:
        this.checkoutService.previousRedirectUrl ||
        this.localizeRouterService
          .translateRoute(`/attendee.show-activity/${this.activity?.sku}`)
          .toString(),
    };
  }

  public get isDisable(): boolean {
    return this.sportsNumber !== this.selectedSport?.length;
  }

  public get sportsNumber(): string {
    return this.offer?.meta?.maximumSportSelectable as unknown as string;
  }

  public get sportsNumberTranslation(): string {
    return Number(this.sportsNumber) > 1
      ? this.translateService.instant(
          'participant_booking.birthday.sport.selection.title',
          {
            sportsNumber: Number(this.sportsNumber) || 2,
          }
        )
      : this.translateService.instant(
          'participant_booking.birthday.sport.selection.title_single'
        );
  }

  private addCheckbox(sport): void {
    let sportsSelected;
    this.checkoutService.getItems().subscribe((checkoutItems) => {
      (checkoutItems || []).forEach(
        (item) => (sportsSelected = item.meta?.sports)
      );
    });

    this.birthdaySportForm.push(
      new FormGroup({
        sport: new FormControl(sport),
        checked: new FormControl(sportsSelected?.includes(sport.name)),
      })
    );
  }

  public onSubmit(): void {
    this.checkoutService.addCheckoutItem({
      activity: this.activity,
      defaultOffer: this.offer.identifier,
      timeslot: this.timeslot,
      meta: { sports: this.selectedSport },
    });

    this.redirectionService.redirect('/checkout.subscription', {
      queryParams: {
        sku: this.timeslot.identifier,
        offer: this.offer.identifier,
      },
    });
  }
}
